import { computed, effect, Injectable, signal } from '@angular/core';
import { Photo } from '@model/photo';
import { appDatabase } from '@shared/database/app-database';
import { from, map, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PhotoService {
  private _favorites = signal<Photo[] | undefined>(undefined);
  private _photoTable = appDatabase.photo;

  favorites = computed<Photo[]>(() => this._favorites() ?? []);

  constructor() {
    effect(() => {
      const favorites = this._favorites();

      void this.processTableItems(favorites);
    });
  }

  private async processTableItems(favorites?: Photo[]): Promise<void> {
    // No processing when _favorites() is undefined
    // Otherwise the stored data is deleted on app init
    if (favorites !== undefined) {
      const storedFavorites = await this._photoTable.toArray();
      const deletedFavorites = storedFavorites.filter(
        storedFavorite =>
          favorites.find(favorite => favorite.uuid === storedFavorite.uuid) ===
          undefined,
      );
      const deletedFavoriteUuids = deletedFavorites.map(
        storedFavorite => storedFavorite.uuid,
      );
      await this._photoTable.bulkDelete(deletedFavoriteUuids);

      await this._photoTable.bulkPut(favorites);
    }
  }

  init(): Observable<void> {
    // Get all favorite photos from the database and set the _favorites signal
    return from(this._photoTable.toArray()).pipe(
      tap(favorites => this._favorites.set(favorites)),
      map(() => undefined),
    );
  }

  onFavorite(photo: Photo): void {
    const favorites = this._favorites() ?? [];

    // Check if the given photo is a favorite
    const isFavorite =
      favorites.find(favorite => favorite.uuid === photo.uuid) !== undefined;

    if (isFavorite) {
      // Remove the available favorite
      this._favorites.set(
        favorites.filter(favorite => favorite.uuid !== photo.uuid),
      );
    } else {
      // Add the given photo as new favorite
      this._favorites.set([...favorites, photo]);
    }
  }
}
